import { useCallback, useEffect, useState } from "react";
import { Link, useHistory, useLocation, useParams, useRouteMatch } from "react-router-dom";
import { ChevronLeftIcon } from "@heroicons/react/outline";
import { OAuthError, putData } from "../backend";
import { WorkflowContext, WorkflowOnlyContext, useModal } from "../contexts";
import OAuthModal from "./OAuthModal";
import WorkflowOverview from "./WorkflowOverview";
import WorkflowDocument from "./WorkflowDocument";
import WorkflowSource from "./WorkflowSource";
import WorkflowOutputs from "./WorkflowOutputs";
import UsagePanel from "./UsagePanel";
import WorkflowSignatureRequest from "./WorkflowSignatureRequest";
import WorkflowApprovals from "./WorkflowApprovals";
import ErrorModal from "./ErrorModal";
import useWorkflow from "../hooks/useWorkflow";
import WorfklowAutomationsList from "./WorkflowAutomationsList";
import WorkflowDetailNavigation from "./WorkflowDetailNavigation";
import WorkflowEmail from "./WorkflowEmail";
import WorkflowDocumentSignatureOnly from "./WorkflowDocumentSignatureOnly";
import WorkflowOverviewSignatureOnly from "./WorkflowOverviewSignatureOnly";
import WorkflowSidebar from "./WorkflowSidebar";
import { useTranslation } from "react-i18next";

interface Props {
  contentOffset: number
}

function WorkflowDetail(props: Props) {
  const history = useHistory();
  const location = useLocation();

  const { url } = useRouteMatch();
  const { contentOffset } = props;
  const { t } = useTranslation();

  const { openModal } = useModal();
  const [sourceSettingsPortalNode, setSourceSettingsPortalNode] = useState<HTMLDivElement | null>(null);

  const { id } = useParams<{ id: string | undefined }>();

  const { workflow, mutateWorkflow, error } = useWorkflow(id ?? "__latest");

  const [automationOptions, setAutomationOptions] = useState<any>({ ...(workflow?.latestAutomation?.options ?? { mode: "ALL", overwriteOutputs: false }) });

  const overviewRegular = localStorage.getItem("overviewRegular") === "true";

  useEffect(() => {
    if (!workflow)
      return;

    //@ts-ignore
    const dataLayer = window.dataLayer;
    dataLayer.push({ 'workflow_id': workflow.id, 'workflow_name': workflow.name, source_type: workflow.source?.sourceType, document_type: workflow.documents[0]?.documentType });
  }, [workflow]);

  useEffect(() => {
    const pageTitle = `${workflow?.name} | Portant`;
    if (workflow?.name && document.title !== pageTitle)
      document.title = pageTitle;

    return (() => { document.title = "Portant" })
  }, [workflow?.name])

  function updateAutoCreate(value: boolean) {
    const updatedWorkflow = { ...workflow!, autoCreate: value };
    mutateWorkflow(updatedWorkflow, { revalidate: false });

    putData(`/workflows/${workflow?.id}/auto-create/`, { autoCreate: value })
      .then(() => mutateWorkflow(updatedWorkflow, { revalidate: true }))
      .catch(error => {
        if (error instanceof OAuthError)
          openModal(<OAuthModal oauthUrl={error.oauthUrl} />)
        else
          openModal(<ErrorModal details={error} />)
      });
  }


  const updateAutomationOptions = useCallback((options: any) =>
    setAutomationOptions((current: any) => ({ ...current, ...options })), [setAutomationOptions]);

  if (!workflow)
    return (
      <div className='flex flex-col font-sans bg-gray-100' style={{ height: `calc(100vh - ${contentOffset}px)` }}>
        <div className='flex flex-row justify-start py-2 px-4 bg-white shadow z-10 h-14' >
          <ChevronLeftIcon className="h-6 mr-2 my-auto text-gray-600 hover:text-blue cursor-pointer" onClick={() => history.push("/")} />
        </div>
        {!error &&
          <div className='m-auto w-24 h-24'>
            <img src="https://static.portant.co/portant-loading-white.svg" alt="Loading Logo" />
          </div>
        }
        {error &&
          <div className="flex flex-col items-center m-auto">
            <div className="flex flex-col items-center">
              <div className="font-gilroy font-semibold text-gray-600 text-4xl">
                {t("Workflow not Found")}
              </div>
              <div className="font-gilroy text-gray-600">
                {t("This workflow may have been moved or deleted")}
              </div>
            </div>
            <Link to="/workflows" className="underline text-gray-600 text-sm font-semibold mt-4">{t("Return to Workflows")}</Link>
          </div>
        }
      </div>
    )

  const workflowContext = { workflow, mutateWorkflow, updateAutomationOptions, updateAutoCreate, automationOptions, sourceSettingsPortalNode };

  return (
    <WorkflowContext.Provider value={workflowContext}>
      <WorkflowOnlyContext.Provider value={{ workflow }}>
        <div className='relative flex flex-col font-sans bg-gray-100' style={{ height: `calc(100vh - ${contentOffset}px)` }}>
          <div className='bg-white shadow z-10 h-16'>
            <div className=" px-4 flex flex-row items-center justify-evenly h-full">
              <div className="w-full flex flex-row items-center">
                <div className="flex items-center gap-1 text-sm font-gilroy font-semibold">
                  <Link to="/workflows" className="flex items-center gap-1 text-gray-400 hover:text-blue hover:underline">
                    <ChevronLeftIcon className="h-5 w-5" />
                    {t("Workflows")}
                  </Link>
                  <span className="text-gray-300">/</span>
                  <span className="text-gray-600 truncate max-w-[250px]">{workflow.name}</span>
                </div>
              </div>

              <WorkflowDetailNavigation workflow={workflow} />

              <div className="w-full flex flex-row justify-end items-center">
                <UsagePanel className="w-[328px]" />
              </div>
            </div>
          </div>
          <div className="flex flex-1 overflow-hidden">
            <div className="relative w-full h-[calc(100vh-86px)] lg:h-[calc(100vh-64px)]">
              {(workflow.source?.sourceType === "SIGNATURE_LINK" && !overviewRegular) &&
                <>
                  <WorkflowOverviewSignatureOnly show={url === location.pathname} contentOffset={contentOffset} />
                  <WorkflowSource />
                  <WorkflowDocumentSignatureOnly />
                </>
              }
              {!(workflow.source?.sourceType === "SIGNATURE_LINK" && !overviewRegular) &&
                <>
                  <WorkflowOverview show={url === location.pathname} contentOffset={contentOffset} />
                  <WorkflowSource />
                  <WorkflowDocument />
                  <WorkflowEmail />
                  <WorkflowSignatureRequest />
                  <WorkflowApprovals />
                </>
              }
              {location.pathname.endsWith("/outputs") &&
                <div className="flex h-full">
                  <div className="m-4 w-full">
                    <WorkflowOutputs contentOffset={contentOffset} workflow={workflow} mutateWorkflow={mutateWorkflow} />
                  </div>
                </div>
              }
              <WorfklowAutomationsList contentOffset={contentOffset} />
            </div>
            {(url === location.pathname || location.pathname.endsWith("/source") || location.pathname.endsWith("/outputs")) &&
              <WorkflowSidebar defaultShowOutputLocations={location.pathname.endsWith("/outputs")} setSourceSettingsRef={setSourceSettingsPortalNode} />
            }
          </div>
        </div>
      </WorkflowOnlyContext.Provider>
    </WorkflowContext.Provider>
  );

}

export default WorkflowDetail;
